// SEO utility functions for managing meta tags and structured data

interface SEOConfig {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  structuredData?: object;
}

export const updateSEO = (config: SEOConfig) => {
  // Update title
  document.title = config.title;

  // Update meta tags
  updateMetaTag('description', config.description);
  updateMetaTag('keywords', config.keywords);
  
  // Update canonical
  updateLinkTag('canonical', config.canonical);
  
  // Update Open Graph tags
  updateMetaTag('og:title', config.ogTitle || config.title, 'property');
  updateMetaTag('og:description', config.ogDescription || config.description, 'property');
  updateMetaTag('og:type', config.ogType || 'website', 'property');
  updateMetaTag('og:url', config.canonical, 'property');
  
  // Update Twitter Card tags
  updateMetaTag('twitter:title', config.ogTitle || config.title);
  updateMetaTag('twitter:description', config.ogDescription || config.description);
  
  // Update last-modified
  const today = new Date().toISOString().split('T')[0];
  updateMetaTag('last-modified', today);
  
  // Update structured data
  if (config.structuredData) {
    updateStructuredData(config.structuredData);
  }
};

const updateMetaTag = (name: string, content: string, attribute: string = 'name') => {
  let element = document.querySelector(`meta[${attribute}="${name}"]`);
  if (element) {
    element.setAttribute('content', content);
  } else {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    element.setAttribute('content', content);
    document.head.appendChild(element);
  }
};

const updateLinkTag = (rel: string, href: string) => {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  if (element) {
    element.href = href;
  } else {
    element = document.createElement('link');
    element.rel = rel;
    element.href = href;
    document.head.appendChild(element);
  }
};

const updateStructuredData = (data: object) => {
  // Remove existing structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }
  
  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
};

// Get site URL from environment variable
const getSiteUrl = () => {
  return import.meta.env.VITE_SITE_URL || 'https://example.com';
};

// SEO configurations for each page
export const seoConfigs = {
  home: {
    title: '岡山市の脱毛サロン・エステ｜20代女性のための美肌体験｜おかやま脱毛サロン・エステ',
    description: '岡山市で20代女性に選ばれる脱毛サロン・エステ。丁寧なカウンセリングと確かな技術で、あなただけの美肌体験を提供します。全身脱毛、フェイシャルエステ、痩身ボディケアなど豊富なメニュー。初回カウンセリング無料。営業時間9:00-18:00、日祝定休。',
    keywords: '岡山市,脱毛サロン,エステ,20代女性,美肌,全身脱毛,フェイシャル,痩身',
    canonical: `${getSiteUrl()}/`,
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BeautySalon',
          name: 'おかやま脱毛サロン・エステ株式会社',
          description: '岡山市で20代女性に選ばれる脱毛サロン・エステ',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '北区○○町1-2-3',
            addressLocality: '岡山市',
            addressRegion: '岡山県',
            postalCode: '700-0000',
            addressCountry: 'JP',
          },
          telephone: '+81-86-012-3456',
          url: getSiteUrl(),
          openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            opens: '09:00',
            closes: '18:00',
          },
          priceRange: '¥¥',
          geo: {
            '@type': 'GeoCoordinates',
            latitude: '34.6617',
            longitude: '133.9350',
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: '初回カウンセリングは無料ですか？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'はい、初回カウンセリングは無料です。お肌の状態やご希望をじっくりお伺いし、最適なプランをご提案させていただきます。',
              },
            },
            {
              '@type': 'Question',
              name: '痛みはありますか？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '最新の脱毛機器を使用しており、痛みを最小限に抑えた施術が可能です。個人差はありますが、多くのお客様から「思ったより痛くなかった」とのお声をいただいています。',
              },
            },
            {
              '@type': 'Question',
              name: '予約のキャンセルは可能ですか？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '前日までのご連絡でキャンセル可能です。当日キャンセルの場合はキャンセル料が発生する場合がございますので、予めご了承ください。',
              },
            },
            {
              '@type': 'Question',
              name: '施術時間はどのくらいですか？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'コースによって異なりますが、全身脱毛で約90分、フェイシャルエステで約60分が目安です。初回はカウンセリング時間を含めて余裕を持ってお越しください。',
              },
            },
            {
              '@type': 'Question',
              name: '支払い方法は何がありますか？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '現金、クレジットカード、電子マネーに対応しております。分割払いのご相談も承っておりますので、お気軽にお問い合わせください。',
              },
            },
            {
              '@type': 'Question',
              name: '駐車場はありますか？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'はい、店舗前に専用駐車場を3台分ご用意しております。満車の場合は近隣のコインパーキングをご利用ください。',
              },
            },
          ],
        },
      ],
    },
  },
  about: {
    title: '私たちについて｜岡山市の脱毛サロン・エステ｜おかやま脱毛サロン・エステ',
    description: '岡山市で20代女性の美しさをサポートする専門サロン。丁寧なカウンセリングと確かな技術で、お客様の理想の美しさを実現します。経験豊富なスタッフが、脱毛からフェイシャルエステ、痩身ケアまでトータルビューティーをサポート。',
    keywords: '岡山市,脱毛サロン,エステ,会社概要,スタッフ紹介,美容サロン',
    canonical: `${getSiteUrl()}/about`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '私たちについて',
      description: '岡山市で20代女性の美しさをサポートする専門サロン',
      url: `${getSiteUrl()}/about`,
      mainEntity: {
        '@type': 'BeautySalon',
        name: 'おかやま脱毛サロン・エステ株式会社',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '北区○○町1-2-3',
          addressLocality: '岡山市',
          addressRegion: '岡山県',
          postalCode: '700-0000',
          addressCountry: 'JP',
        },
      },
    },
  },
  services: {
    title: 'サービス・料金案内｜岡山市の脱毛サロン・エステ｜おかやま脱毛サロン・エステ',
    description: '岡山市で20代女性に人気の脱毛・エステメニュー。全身脱毛、部分脱毛、フェイシャルエステ、美白フェイシャル、痩身ボディケア、ブライダルコースなど豊富なプラン。明確な料金体系で安心。月額5,980円から。初回カウンセリング無料。',
    keywords: '岡山市,脱毛サロン,エステ,料金,プラン,全身脱毛,フェイシャル,痩身',
    canonical: `${getSiteUrl()}/services`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: '脱毛・エステサービス',
      description: '岡山市で20代女性に人気の脱毛・エステメニュー',
      provider: {
        '@type': 'BeautySalon',
        name: 'おかやま脱毛サロン・エステ株式会社',
      },
      areaServed: {
        '@type': 'City',
        name: '岡山市',
      },
      offers: [
        {
          '@type': 'Offer',
          name: '全身脱毛コース',
          price: '9800',
          priceCurrency: 'JPY',
          description: '最新の脱毛機器を使用した、痛みの少ない全身脱毛',
        },
        {
          '@type': 'Offer',
          name: 'フェイシャルエステ',
          price: '8000',
          priceCurrency: 'JPY',
          description: 'お肌の悩みに合わせたオーダーメイドケア',
        },
        {
          '@type': 'Offer',
          name: '痩身ボディケア',
          price: '15000',
          priceCurrency: 'JPY',
          description: '理想のボディラインを目指す専門的なプログラム',
        },
      ],
    },
  },
  company: {
    title: '会社概要・アクセス｜岡山市の脱毛サロン・エステ｜おかやま脱毛サロン・エステ',
    description: 'おかやま脱毛サロン・エステ株式会社の会社情報。岡山県岡山市北区に所在。JR岡山駅より徒歩10分。専用駐車場完備。営業時間9:00-18:00、日祝定休。清潔で快適な施設で、リラックスしてお過ごしいただけます。',
    keywords: '岡山市,脱毛サロン,エステ,会社概要,アクセス,地図,営業時間',
    canonical: `${getSiteUrl()}/company`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'おかやま脱毛サロン・エステ株式会社',
      url: getSiteUrl(),
      logo: `${getSiteUrl()}/vite.svg`,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '北区○○町1-2-3',
        addressLocality: '岡山市',
        addressRegion: '岡山県',
        postalCode: '700-0000',
        addressCountry: 'JP',
      },
      telephone: '+81-86-012-3456',
      faxNumber: '+81-86-012-3457',
      foundingDate: '2026-04',
      numberOfEmployees: 5,
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '34.6617',
        longitude: '133.9350',
      },
    },
  },
  contact: {
    title: '予約・お問い合わせ｜岡山市の脱毛サロン・エステ｜おかやま脱毛サロン・エステ',
    description: '岡山市の脱毛サロン・エステへのご予約・お問い合わせはこちら。お電話（086-012-3456）またはフォームから受付。営業時間9:00-18:00、日祝定休。初回カウンセリング無料。お気軽にご連絡ください。',
    keywords: '岡山市,脱毛サロン,エステ,予約,お問い合わせ,カウンセリング,電話',
    canonical: `${getSiteUrl()}/contact`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: '予約・お問い合わせ',
      description: '岡山市の脱毛サロン・エステへのご予約・お問い合わせ',
      url: `${getSiteUrl()}/contact`,
      mainEntity: {
        '@type': 'BeautySalon',
        name: 'おかやま脱毛サロン・エステ株式会社',
        telephone: '+81-86-012-3456',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '北区○○町1-2-3',
          addressLocality: '岡山市',
          addressRegion: '岡山県',
          postalCode: '700-0000',
          addressCountry: 'JP',
        },
      },
    },
  },
};
